// @ts-nocheck
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

const DYE_COLORS = {
  white: "#f9fffe",
  orange: "#f9801d",
  magenta: "#c74ebd",
  light_blue: "#3ab3da",
  yellow: "#fed83d",
  lime: "#80c71f",
  pink: "#f38baa",
  gray: "#474f52",
  light_gray: "#9d9d97",
  cyan: "#169c9c",
  purple: "#8932b8",
  blue: "#3c44aa",
  brown: "#835432",
  green: "#5e7c16",
  red: "#b02e26",
  black: "#1d1d21"
};

function getDyeColor(blockId) {
  const suffixes = [
    "_wall_banner",
    "_banner",
    "_bed",
    "_shulker_box",
    "_cushion"
  ];

  for (const suffix of suffixes) {
    if (blockId.endsWith(suffix)) {
      const colorName = blockId.slice(0, -suffix.length);
      return DYE_COLORS[colorName] || "#ffffff";
    }
  }

  return "#ffffff";
}

export function getSpecialBlockKind(blockId) {
  if (
    blockId === "chest" ||
    blockId === "trapped_chest" ||
    blockId === "ender_chest" ||
    blockId.endsWith("_copper_chest")
  ) {
    return "chest";
  }

  if (blockId.endsWith("_wall_banner") || blockId.endsWith("_banner")) {
    return "banner";
  }

  if (blockId === "shulker_box" || blockId.endsWith("_shulker_box")) {
    return "shulker_box";
  }

  if (
    blockId.endsWith("_wall_hanging_sign") ||
    blockId.endsWith("_hanging_sign") ||
    blockId.endsWith("_wall_sign") ||
    blockId.endsWith("_sign")
  ) {
    return "sign";
  }

  if (blockId.endsWith("_bed") || blockId === "bed") {
    return "bed";
  }

  if (blockId.endsWith("_cushion")) {
    return "cushion";
  }

  return null;
}

export function isAlwaysSpecialBlock(blockId) {
  const kind = getSpecialBlockKind(blockId);
  return (
    kind === "chest" ||
    kind === "banner" ||
    kind === "shulker_box" ||
    kind === "cushion"
  );
}

function chestTextureStem(blockId) {
  if (blockId === "ender_chest") return "ender";
  if (blockId === "trapped_chest") return "trapped";

  if (blockId.includes("copper_chest")) {
    if (blockId.includes("oxidized")) return "copper_oxidized";
    if (blockId.includes("weathered")) return "copper_weathered";
    if (blockId.includes("exposed")) return "copper_exposed";
    return "copper";
  }

  return "normal";
}

function signWoodType(blockId) {
  return blockId
    .replace(/^waxed_/, "")
    .replace(/_wall_hanging_sign$/, "")
    .replace(/_hanging_sign$/, "")
    .replace(/_wall_sign$/, "")
    .replace(/_sign$/, "");
}

export function getSpecialTextureCandidates(blockId, state = {}) {
  const kind = getSpecialBlockKind(blockId);

  if (kind === "chest") {
    const stem = chestTextureStem(blockId);
    const type =
      stem === "ender" ? "single" : (state.type || "single");
    const suffix =
      type === "left" || type === "right" ? "_" + type : "";
    return ["entity/chest/" + stem + suffix];
  }

  if (kind === "banner") {
    return ["entity/banner/base", "entity/banner_base"];
  }

  if (kind === "shulker_box") {
    if (blockId === "shulker_box") {
      return [
        "entity/shulker/shulker",
        "entity/shulker/shulker_purple"
      ];
    }
    const color = blockId.replace(/_shulker_box$/, "");
    return [
      "entity/shulker/shulker_" + color,
      "entity/shulker/" + color
    ];
  }

  if (kind === "bed") {
    const color = blockId.replace(/_bed$/, "");
    return [
      "entity/bed/" + color,
      color === "light_gray" ? "entity/bed/silver" : ""
    ].filter(Boolean);
  }

  if (kind === "sign") {
    const wood = signWoodType(blockId);
    const hanging = blockId.includes("hanging_sign");

    if (hanging) {
      return [
        "entity/signs/hanging/" + wood,
        "entity/signs/hanging_signs/" + wood,
        "entity/hanging_signs/" + wood,
        "entity/signs/" + wood
      ];
    }

    return [
      "entity/signs/" + wood,
      "entity/sign/" + wood
    ];
  }

  if (kind === "cushion") {
    return [
      "entity/cushion/" + blockId,
      "entity/cushion/" + blockId.replace(/^minecraft:/, "")
    ];
  }

  return [];
}

function uvRectToPoints(rect, width, height) {
  const [u0, v0, u1, v1] = rect;
  return [
    [u0 / width, 1 - v1 / height],
    [u1 / width, 1 - v1 / height],
    [u1 / width, 1 - v0 / height],
    [u0 / width, 1 - v0 / height]
  ];
}

function createCuboidGeometry({
  from,
  to,
  textureWidth,
  textureHeight,
  textureOffset
}) {
  const x1 = from[0] / 16 - 0.5;
  const y1 = from[1] / 16 - 0.5;
  const z1 = from[2] / 16 - 0.5;
  const x2 = to[0] / 16 - 0.5;
  const y2 = to[1] / 16 - 0.5;
  const z2 = to[2] / 16 - 0.5;

  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const u = textureOffset[0];
  const v = textureOffset[1];

  const u0 = u;
  const u1 = u + dz;
  const u2 = u1 + dx;
  const u3 = u2 + dx;
  const u4 = u3 + dz;
  const u5 = u4 + dx;
  const v0 = v;
  const v1 = v + dz;
  const v2 = v1 + dy;

  const faces = [
    {
      corners: [
        [x2, y1, z1],
        [x1, y1, z1],
        [x1, y2, z1],
        [x2, y2, z1]
      ],
      uv: [u1, v1, u2, v2]
    },
    {
      corners: [
        [x1, y1, z2],
        [x2, y1, z2],
        [x2, y2, z2],
        [x1, y2, z2]
      ],
      uv: [u4, v1, u5, v2]
    },
    {
      corners: [
        [x1, y1, z1],
        [x1, y1, z2],
        [x1, y2, z2],
        [x1, y2, z1]
      ],
      uv: [u0, v1, u1, v2]
    },
    {
      corners: [
        [x2, y1, z2],
        [x2, y1, z1],
        [x2, y2, z1],
        [x2, y2, z2]
      ],
      uv: [u2, v1, u4, v2]
    },
    {
      corners: [
        [x1, y2, z2],
        [x2, y2, z2],
        [x2, y2, z1],
        [x1, y2, z1]
      ],
      uv: [u2, v0, u3, v1]
    },
    {
      corners: [
        [x1, y1, z1],
        [x2, y1, z1],
        [x2, y1, z2],
        [x1, y1, z2]
      ],
      uv: [u1, v0, u2, v1]
    }
  ];

  const positions = [];
  const uvs = [];
  const order = [0, 1, 2, 0, 2, 3];

  faces.forEach((face) => {
    const faceUvs = uvRectToPoints(
      face.uv,
      textureWidth,
      textureHeight
    );

    order.forEach((cornerIndex) => {
      positions.push(...face.corners[cornerIndex]);
      uvs.push(...faceUvs[cornerIndex]);
    });
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute(
    "uv",
    new THREE.Float32BufferAttribute(uvs, 2)
  );
  geometry.computeVertexNormals();
  return geometry;
}

function mergeParts(parts) {
  const geometry = mergeGeometries(parts, false);
  if (!geometry) {
    throw new Error("Failed to merge special Minecraft block geometry.");
  }
  parts.forEach((part) => part.dispose());
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function rotateFacing(geometry, facing) {
  const angles = {
    north: 0,
    east: -Math.PI / 2,
    south: Math.PI,
    west: Math.PI / 2
  };
  geometry.rotateY(angles[facing] || 0);
  return geometry;
}

function rotateStanding(geometry, rotation) {
  const steps = Number(rotation);
  if (Number.isFinite(steps)) {
    geometry.rotateY(-steps * (Math.PI * 2 / 16));
  }
  return geometry;
}

function createChestGeometry(state) {
  const parts = [
    createCuboidGeometry({
      from: [1, 1, 1],
      to: [15, 11, 15],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 19]
    }),
    createCuboidGeometry({
      from: [1, 10, 1],
      to: [15, 15, 15],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 0]
    }),
    createCuboidGeometry({
      from: [7, 8, 0],
      to: [9, 12, 1],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 0]
    })
  ];

  return rotateFacing(mergeParts(parts), state.facing || "north");
}

function createBannerGeometry(blockId, state) {
  const wall = blockId.endsWith("_wall_banner");
  const parts = [];

  parts.push(
    createCuboidGeometry({
      from: [-2, 2, 7.5],
      to: [18, 42, 8.5],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 0]
    })
  );
  parts.push(
    createCuboidGeometry({
      from: [-2, 40, 7],
      to: [18, 42, 9],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 42]
    })
  );

  if (!wall) {
    parts.push(
      createCuboidGeometry({
        from: [7, 0, 7],
        to: [9, 42, 9],
        textureWidth: 64,
        textureHeight: 64,
        textureOffset: [44, 0]
      })
    );
  }

  const geometry = mergeParts(parts);

  if (wall) {
    geometry.translate(0, -0.2, -0.42);
    return rotateFacing(geometry, state.facing || "north");
  }

  return rotateStanding(geometry, state.rotation || "0");
}

function createShulkerGeometry(state) {
  const parts = [
    createCuboidGeometry({
      from: [0, 0, 0],
      to: [16, 8, 16],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 28]
    }),
    createCuboidGeometry({
      from: [0, 8, 0],
      to: [16, 16, 16],
      textureWidth: 64,
      textureHeight: 64,
      textureOffset: [0, 0]
    })
  ];

  return rotateFacing(mergeParts(parts), state.facing || "north");
}

function createLegacySignGeometry(blockId, state) {
  const hanging = blockId.includes("hanging_sign");
  const wall =
    blockId.includes("wall_sign") ||
    blockId.includes("wall_hanging_sign");
  const parts = [];

  if (hanging) {
    parts.push(
      createCuboidGeometry({
        from: [1, 3, 7],
        to: [15, 13, 9],
        textureWidth: 64,
        textureHeight: 32,
        textureOffset: [0, 0]
      })
    );
    if (!wall) {
      parts.push(
        createCuboidGeometry({
          from: [3, 13, 7.5],
          to: [4, 18, 8.5],
          textureWidth: 64,
          textureHeight: 32,
          textureOffset: [0, 14]
        }),
        createCuboidGeometry({
          from: [12, 13, 7.5],
          to: [13, 18, 8.5],
          textureWidth: 64,
          textureHeight: 32,
          textureOffset: [0, 14]
        })
      );
    }
  } else {
    parts.push(
      createCuboidGeometry({
        from: [-4, 7, 7],
        to: [20, 19, 9],
        textureWidth: 64,
        textureHeight: 32,
        textureOffset: [0, 0]
      })
    );

    if (!wall) {
      parts.push(
        createCuboidGeometry({
          from: [7, -7, 7],
          to: [9, 7, 9],
          textureWidth: 64,
          textureHeight: 32,
          textureOffset: [0, 14]
        })
      );
    }
  }

  const geometry = mergeParts(parts);

  if (wall) {
    geometry.translate(0, 0, -0.42);
    return rotateFacing(geometry, state.facing || "north");
  }

  return rotateStanding(geometry, state.rotation || "0");
}

function createLegacyBedGeometry(state) {
  const geometry = createCuboidGeometry({
    from: [0, 3, 0],
    to: [16, 9, 16],
    textureWidth: 64,
    textureHeight: 64,
    textureOffset: [0, 0]
  });
  return rotateFacing(geometry, state.facing || "north");
}

function createCushionGeometry() {
  return createCuboidGeometry({
    from: [1, 1, 1],
    to: [15, 7, 15],
    textureWidth: 64,
    textureHeight: 64,
    textureOffset: [0, 0]
  });
}

export class MinecraftSpecialBlockRenderer {
  constructor(assetLoader) {
    this.assetLoader = assetLoader;
    this.geometryCache = new Map();
    this.materialCache = new Map();
  }

  canRender(blockId) {
    return Boolean(getSpecialBlockKind(blockId));
  }

  isAlwaysSpecial(blockId) {
    return isAlwaysSpecialBlock(blockId);
  }

  resolveTexturePath(blockId, state) {
    return this.assetLoader.findTexturePath(
      getSpecialTextureCandidates(blockId, state)
    );
  }

  getMaterial(blockId, state) {
    const texturePath = this.resolveTexturePath(blockId, state);
    if (!texturePath) {
      throw new Error(
        "No version-specific special texture found for " + blockId + "."
      );
    }

    const kind = getSpecialBlockKind(blockId);
    const tint =
      kind === "banner" ? getDyeColor(blockId) : "#ffffff";
    const key = texturePath + "|" + tint;

    if (this.materialCache.has(key)) {
      return this.materialCache.get(key);
    }

    const material = new THREE.MeshStandardMaterial({
      color: tint,
      roughness: 0.88,
      metalness: 0,
      transparent:
        kind === "banner" ||
        kind === "sign" ||
        kind === "cushion",
      alphaTest:
        kind === "banner" ||
        kind === "sign" ||
        kind === "cushion"
          ? 0.05
          : 0,
      side:
        kind === "banner" || kind === "sign"
          ? THREE.DoubleSide
          : THREE.FrontSide
    });

    this.assetLoader
      .loadTexture(texturePath)
      .then((texture) => {
        material.map = texture;
        material.needsUpdate = true;
      })
      .catch((error) => {
        console.warn(
          "Unable to load special Minecraft texture " +
            texturePath +
            ".",
          error
        );
      });

    this.materialCache.set(key, material);
    return material;
  }

  createGeometry(blockId, state) {
    const kind = getSpecialBlockKind(blockId);
    const signature = [
      kind,
      blockId,
      state.facing || "",
      state.rotation || "",
      state.type || "",
      state.part || ""
    ].join("|");

    if (this.geometryCache.has(signature)) {
      return {
        signature,
        geometry: this.geometryCache.get(signature)
      };
    }

    let geometry;
    if (kind === "chest") {
      geometry = createChestGeometry(state);
    } else if (kind === "banner") {
      geometry = createBannerGeometry(blockId, state);
    } else if (kind === "shulker_box") {
      geometry = createShulkerGeometry(state);
    } else if (kind === "sign") {
      geometry = createLegacySignGeometry(blockId, state);
    } else if (kind === "bed") {
      geometry = createLegacyBedGeometry(state);
    } else if (kind === "cushion") {
      geometry = createCushionGeometry();
    } else {
      throw new Error("Unsupported special block: " + blockId);
    }

    this.geometryCache.set(signature, geometry);
    return { signature, geometry };
  }

  async createRenderParts(blockEntry) {
    const state = blockEntry.state || {};
    const { signature, geometry } = this.createGeometry(
      blockEntry.blockId,
      state
    );
    const material = this.getMaterial(blockEntry.blockId, state);

    return [
      {
        signature: "special|" + signature,
        geometry,
        materials: material,
        model: "special:" + getSpecialBlockKind(blockEntry.blockId),
        special: true
      }
    ];
  }

  dispose() {
    this.geometryCache.forEach((geometry) => geometry.dispose());
    this.materialCache.forEach((material) => material.dispose());
  }
}
